import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, filter, forkJoin, map, Observable, of, take } from 'rxjs';

import { CanvasElementFrom } from '@blaze-runtime/engine-domain';

import {
  createRowActionPageFailure,
  createRowActionPageSuccess,
  RowActionPageGenerationFacade,
} from '../../../+state/row-action-page-generation';
import { workflowActions } from '../../../+state/runtime';

import {
  CALENDAR_ROW_ACTIONS_PREGENERATION_TYPES_MAP,
  TABLE_ROW_ACTION_PATHS_MAP,
} from './calendar.actions';
import { CalendarResolvedContext } from './calendar.definition';

@Injectable({
  providedIn: 'root',
})
export class CalendarActionsHandler {
  private readonly actions$ = inject(Actions);
  private readonly rowActionPageGenerationFacade = inject(RowActionPageGenerationFacade);
  private readonly store = inject(Store);

  finalizeRowActionDialogs(
    element: CanvasElementFrom<'calendar'>,
    context: CalendarResolvedContext,
  ): Observable<void> {
    if (!Array.isArray(context.element.actions) || context.element.actions.length === 0) {
      return of(undefined);
    }

    const sources = context.element.actions.map((action) => {
      const path = TABLE_ROW_ACTION_PATHS_MAP.get(action);

      if (!path) {
        return of(undefined);
      }

      const pregenerationType = CALENDAR_ROW_ACTIONS_PREGENERATION_TYPES_MAP.get(action);

      if (!pregenerationType) {
        console.error(`No pregeneration type found for action ${action}`);
        return of(undefined);
      }

      return defer(() => {
        this.rowActionPageGenerationFacade.tryCreateRowActionPage(element.id, {
          actionPath: path,
          pregenerationType,
        });

        return this.actions$.pipe(
          ofType(createRowActionPageSuccess, createRowActionPageFailure),
          filter(
            (action) =>
              action.elementId === element.id && action.pregenerationType === pregenerationType,
          ),
          take(1),
        );
      });
    });

    return forkJoin(sources).pipe(map(() => undefined));
  }

  finalizeRowActionWorkflows(
    element: CanvasElementFrom<'calendar'>,
    context: CalendarResolvedContext,
  ): Observable<void> {
    if (context.element.actions?.every((action) => action !== 'Delete')) {
      return of(undefined);
    }

    return defer(() => {
      this.store.dispatch(
        workflowActions.generateDeleteRowWorkflow({
          elementId: element.id,
          actionPath: 'content.itemDelete',
        }),
      );

      return this.actions$.pipe(
        ofType(
          workflowActions.createElementWorkflowSuccess,
          workflowActions.createElementWorkflowFailed,
        ),
        filter(
          (action) =>
            action.elementId === element.id && !!action.workflowIdPath?.includes('itemDelete'),
        ),
        take(1),
        map(() => undefined),
      );
    });
  }
}
